import React, { useState } from "react";
import { X, Copy, Check, Loader, Share2, Download } from "lucide-react";
import { IProduct } from "../types";
import { apiService } from "../services/api";
import { toNumber } from "../utils/price";

interface ShareBuildModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedComponents: Record<string, IProduct>;
  onLoadBuild: (components: Record<string, IProduct>) => void;
}

export const ShareBuildModal: React.FC<ShareBuildModalProps> = ({
  isOpen,
  onClose,
  selectedComponents,
  onLoadBuild,
}) => {
  const [shareCode, setShareCode] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [loadCodeInput, setLoadCodeInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatCodeForDisplay = (code: string): string => {
    return `BLD-${code}`;
  };

  const parseCodeFromInput = (input: string): string => {
    // Remove "BLD-" prefix if present, trim whitespace, convert to uppercase
    return input.trim().toUpperCase().replace(/^BLD-/, "");
  };

  const handleGenerateCode = async () => {
    if (Object.keys(selectedComponents).length === 0) {
      setGenerateError("No components in build");
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);
    setCopied(false);

    try {
      const products = Object.entries(selectedComponents).map(
        ([categoryId, product]) => ({
          productId: product.id,
          categoryId,
        }),
      );

      const totalPrice = Object.values(selectedComponents).reduce(
        (sum, product) => sum + toNumber(product.price),
        0,
      );

      const response = await apiService.saveBuild(products, totalPrice);
      setShareCode(response.code);
    } catch (error) {
      console.error("Failed to generate share code:", error);
      setGenerateError("Failed to generate share code. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyCode = async () => {
    if (!shareCode) return;

    try {
      await navigator.clipboard.writeText(formatCodeForDisplay(shareCode));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleLoadBuild = async () => {
    const code = parseCodeFromInput(loadCodeInput);

    if (!code || code.length !== 6) {
      setLoadError("Please enter a valid 6-character build code");
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const buildData = await apiService.getBuildByCode(code);

      // Fetch full product data for the build
      const productIds = buildData.products.map((p) => ({
        productId: p.productId,
        categoryId: p.categoryId,
      }));

      const components = await apiService.getProductsByIds(productIds);

      if (Object.keys(components).length === 0) {
        setLoadError("Could not load products for this build");
        return;
      }

      onLoadBuild(components);
      onClose();
    } catch (error: any) {
      console.error("Failed to load build:", error);
      if (error.response?.status === 404) {
        setLoadError("Build not found. Please check the code and try again.");
      } else {
        setLoadError("Failed to load build. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const componentCount = Object.keys(selectedComponents).length;

  return (
    <div
      className="srb-fixed srb-inset-0 srb-z-modal srb-flex srb-items-center srb-justify-center srb-p-4"
      style={{ backgroundColor: "var(--srb-overlay-bg)" }}
      onClick={handleBackdropClick}
    >
      <div
        className="srb-bg-background srb-border srb-border-subtle srb-w-full srb-max-w-md srb-overflow-hidden srb-flex srb-flex-col"
        style={(function () {
          const root = document.querySelector(".sim-rig-builder");
          if (!root) return {};
          const borderColor = getComputedStyle(root)
            .getPropertyValue("--srb-secondary-color")
            .trim();
          return { boxShadow: `0 0 0 1px ${borderColor}` };
        })()}
      >
        <div className="srb-flex srb-justify-between srb-items-center srb-p-4 md:srb-p-6 srb-border-b srb-border-subtle">
          <h2 className="srb-text-xl srb-font-bold srb-text-main">
            Share Build
          </h2>
          <button
            onClick={onClose}
            className="srb-p-2 srb-border-none !srb-bg-transparent srb-text-main hover:srb-text-primary srb-cursor-pointer srb-transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="srb-p-4 md:srb-p-6 srb-space-y-6">
          {/* Share Section */}
          <div className="srb-space-y-3">
            <div className="srb-flex srb-items-center srb-gap-2">
              <Share2 size={18} className="srb-text-primary" />
              <h3 className="srb-text-sm !srb-m-0 srb-font-semibold srb-text-main srb-uppercase srb-tracking-wider">
                Share Your Build
              </h3>
            </div>

            {shareCode ? (
              <div className="srb-space-y-3">
                <div className="srb-flex srb-items-center srb-gap-2 srb-bg-hover srb-p-3 srb-border srb-border-subtle">
                  <span className="srb-flex-1 srb-font-mono srb-text-lg srb-text-main srb-tracking-wider">
                    {formatCodeForDisplay(shareCode)}
                  </span>
                  <button
                    onClick={handleCopyCode}
                    className="srb-p-2 srb-border-none srb-bg-primary hover:srb-opacity-90 srb-text-white srb-cursor-pointer srb-transition srb-flex srb-items-center srb-gap-1"
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                    <span className="srb-text-sm">
                      {copied ? "Copied!" : "Copy"}
                    </span>
                  </button>
                </div>
                <p className="srb-text-xs srb-text-muted">
                  Share this code with others to let them load your exact build
                </p>
              </div>
            ) : (
              <div className="srb-space-y-3">
                <p className="srb-text-sm srb-text-muted">
                  Generate a shareable code for your current build (
                  {componentCount} component{componentCount !== 1 ? "s" : ""})
                </p>
                <button
                  onClick={handleGenerateCode}
                  disabled={isGenerating || componentCount === 0}
                  className="srb-w-full srb-py-3 srb-px-4 srb-border-none srb-bg-primary hover:srb-opacity-90 disabled:srb-opacity-50 srb-text-white srb-font-semibold srb-cursor-pointer disabled:srb-cursor-not-allowed srb-transition srb-flex srb-items-center srb-justify-center srb-gap-2"
                >
                  {isGenerating ? (
                    <>
                      <Loader className="srb-animate-spin" size={18} />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Share2 size={18} />
                      Generate Share Code
                    </>
                  )}
                </button>
                {generateError && (
                  <p className="srb-text-sm srb-text-red-400">
                    {generateError}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="srb-border-t srb-border-subtle" />

          {/* Load Section */}
          <div className="srb-space-y-3">
            <div className="srb-flex srb-items-center srb-gap-2">
              <Download size={18} className="srb-text-primary" />
              <h3 className="srb-text-sm !srb-m-0 srb-font-semibold srb-text-main srb-uppercase srb-tracking-wider">
                Load a Build
              </h3>
            </div>

            <p className="srb-text-sm srb-text-muted">
              Enter a build code to load a saved build
            </p>

            <div className="srb-flex srb-gap-2">
              <input
                type="text"
                value={loadCodeInput}
                onChange={(e) => {
                  setLoadCodeInput(e.target.value.toUpperCase());
                  setLoadError(null);
                }}
                placeholder="e.g., BLD-X7K9M2"
                maxLength={10}
                className="srb-flex-1 srb-px-3 srb-py-2 srb-bg-hover srb-border srb-border-subtle srb-text-main srb-font-mono srb-text-base srb-tracking-wider placeholder:srb-text-muted"
              />
              <button
                onClick={handleLoadBuild}
                disabled={isLoading || !loadCodeInput.trim()}
                className="srb-px-4 srb-py-2 srb-border-none srb-bg-primary hover:srb-opacity-90 disabled:srb-opacity-50 srb-text-white srb-font-semibold srb-cursor-pointer disabled:srb-cursor-not-allowed srb-transition srb-flex srb-items-center srb-gap-1"
              >
                {isLoading ? (
                  <Loader className="srb-animate-spin" size={18} />
                ) : (
                  "Load"
                )}
              </button>
            </div>

            {loadError && (
              <p className="srb-text-sm srb-text-red-400">{loadError}</p>
            )}
          </div>
        </div>

        <div className="srb-p-4 md:srb-p-6 srb-border-t srb-border-subtle srb-bg-hover">
          <button
            onClick={onClose}
            className="srb-w-full srb-px-6 srb-cursor-pointer srb-border-none srb-py-2 srb-bg-hover hover:srb-bg-background srb-border srb-border-subtle srb-text-main srb-font-semibold srb-transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
